import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useJoinCodeVerification } from "../hooks/useJoinCodeVerification";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Plus, Search, LogOut, User, Clock, Building2, Key, DoorOpen, Layers,
  Wrench, Zap, Flame, Wifi, ChefHat, HelpCircle, 
  CheckCircle, AlertCircle, Calendar, ArrowRight
} from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

const categoryIcons = {
  sanitair: <Wrench className="w-4 h-4" />,
  elektriciteit: <Zap className="w-4 h-4" />,
  verwarming: <Flame className="w-4 h-4" />,
  internet: <Wifi className="w-4 h-4" />,
  keuken: <ChefHat className="w-4 h-4" />,
  anders: <HelpCircle className="w-4 h-4" />
};

const statusLabels = {
  sent: "Verstuurd",
  received: "Ontvangen",
  in_progress: "In Behandeling",
  resolved: "Opgelost"
};

const StudentDashboard = () => {
  const { user, logout, authAxios, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [studentUnread, setStudentUnread] = useState({});
  
  // Join property dialog
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [joinData, setJoinData] = useState({ join_code: "", room_number: "", floor: "" });
  const [joining, setJoining] = useState(false);
  const { propertyInfo: propertyPreview, verifyJoinCode: verifyJoinCodeRaw, setPropertyInfo: setPropertyPreview } = useJoinCodeVerification();
  const verifyJoinCode = () => verifyJoinCodeRaw(joinData.join_code).catch(() => {});

  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchUnreadCounts = async () => {
    try {
      const res = await authAxios.get('/tickets/unread-counts-student');
      setStudentUnread(res.data);
    } catch (err) {
      console.error('Unread counts error:', err);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await authAxios.get("/tickets");
      setTickets(response.data);
      await fetchUnreadCounts();
    } catch (error) {
      toast.error("Kon meldingen niet laden");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("U bent uitgelogd");
  };


  const handleJoinProperty = async () => {
    if (!joinData.join_code || !joinData.room_number || !joinData.floor) {
      toast.error("Vul alle velden in");
      return;
    }
    
    setJoining(true);
    try {
      await authAxios.post("/properties/join", joinData);
      await refreshUser();
      setShowJoinDialog(false);
      setJoinData({ join_code: "", room_number: "", floor: "" });
      setPropertyPreview(null);
      toast.success("Succesvol aangesloten bij pand!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon niet aansluiten");
    } finally {
      setJoining(false);
    }
  };

  const filteredTickets = tickets
    .filter(ticket =>
      ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aResolved = a.status === 'resolved' ? 1 : 0;
      const bResolved = b.status === 'resolved' ? 1 : 0;
      if (aResolved !== bResolved) return aResolved - bResolved;

      const aUnread = studentUnread[a.id];
      const bUnread = studentUnread[b.id];
      const aHasNotif = aUnread ? (aUnread.chat_count > 0 || aUnread.has_update) : false;
      const bHasNotif = bUnread ? (bUnread.chat_count > 0 || bUnread.has_update) : false;
      if (aHasNotif !== bHasNotif) return aHasNotif ? -1 : 1;

      return new Date(b.created_at) - new Date(a.created_at);
    });

  const openTickets = tickets.filter(t => t.status !== 'resolved').length;
  const resolvedTickets = tickets.filter(t => t.status === 'resolved').length;

  return (
    <div className="min-h-screen bg-[#0B0A14]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="text-xl font-bold text-white font-['Outfit']">
                Kot<span className="text-indigo-500">Klusser</span>
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link 
                to="/profiel" 
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
                data-testid="profile-link"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline group-hover:text-indigo-400">{user?.name}</span>
              </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Welcome section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
              Hallo, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-400 mt-1">Beheer uw defectmeldingen</p>
          </motion.div>

          {/* Property Info Card */}
          {user?.property_id ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="bg-[#161425] border border-white/5 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{user.property_name}</p>
                  <p className="text-sm text-slate-400">
                    Kamer {user.room_number} • Verdieping {user.floor}
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Key className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Nog niet gekoppeld aan een pand</p>
                    <p className="text-sm text-slate-400">
                      Vraag uw verhuurder om een uitnodigingscode
                    </p>
                  </div>
                </div>
                <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="join-property-btn">
                      Aansluiten
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#161425] border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white">Aansluiten bij pand</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Uitnodigingscode</Label>
                        <Input
                          value={joinData.join_code}
                          onChange={(e) => setJoinData({ ...joinData, join_code: e.target.value.toUpperCase() })}
                          onBlur={verifyJoinCode}
                          placeholder="Bijv. ABC123"
                          maxLength={6}
                          className="bg-[#1C1A2E] border-white/10 text-white placeholder:text-slate-500 uppercase"
                          data-testid="join-code-input"
                        />
                        {propertyPreview && (
                          <p className="text-sm text-emerald-400">
                            ✓ Pand gevonden: {propertyPreview.property_name}
                          </p>
                        )}
                      </div>
                      
                      {propertyPreview && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-slate-300">Kamernummer</Label>
                            <div className="relative">
                              <DoorOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                              <Input
                                value={joinData.room_number}
                                onChange={(e) => setJoinData({ ...joinData, room_number: e.target.value })}
                                placeholder="Bijv. 101"
                                className="pl-10 bg-[#1C1A2E] border-white/10 text-white placeholder:text-slate-500"
                                data-testid="join-room-input"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-300">Verdieping</Label>
                            <Select
                              value={joinData.floor}
                              onValueChange={(value) => setJoinData({ ...joinData, floor: value })}
                              data-testid="join-floor-input"
                            >
                              <SelectTrigger className="bg-[#1C1A2E] border-white/10 text-white h-12">
                                <SelectValue placeholder="Selecteer verdieping" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#161425] border-white/10">
                                {propertyPreview?.floors?.map((floor) => (
                                  <SelectItem key={floor.value} value={floor.value} className="text-white">
                                    {floor.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowJoinDialog(false)}
                        className="border-white/10 text-white"
                      >
                        Annuleren
                      </Button>
                      <Button
                        onClick={handleJoinProperty}
                        disabled={joining || !propertyPreview}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="confirm-join-btn"
                      >
                        {joining ? "Aansluiten..." : "Aansluiten"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </motion.div>
          )}

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            <div className="bg-[#161425] border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{openTickets}</p>
                  <p className="text-sm text-slate-400">Open meldingen</p>
                </div>
              </div>
            </div>
            <div className="bg-[#161425] border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{resolvedTickets}</p>
                  <p className="text-sm text-slate-400">Opgelost</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mb-6"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="text"
                placeholder="Zoek op titel of ticketnummer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#161425] border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500"
                data-testid="search-input"
              />
            </div>
          </motion.div>

          {/* Tickets list */}
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              </div>
            ) : filteredTickets.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#161425] flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-slate-500" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Geen meldingen gevonden</h3>
                <p className="text-slate-400 mb-6">
                  {searchQuery ? "Probeer een andere zoekterm" : "Begin door een nieuwe melding aan te maken"}
                </p>
                {!searchQuery && (
                  <Link to="/nieuw-melding">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="empty-new-report-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nieuwe melding
                    </Button>
                  </Link>
                )}
              </motion.div>
            ) : (
              filteredTickets.map((ticket, idx) => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                >
                  <Link to={`/ticket/${ticket.id}`}>
                    <div 
                      className="bg-[#161425] border border-white/5 rounded-xl p-4 hover:border-indigo-500/30 transition-colors group"
                      data-testid={`ticket-card-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`category-${ticket.category}`}>
                              {categoryIcons[ticket.category]}
                            </span>
                            <span className="text-xs text-slate-500 font-mono">
                              {ticket.ticket_number}
                            </span>
                          </div>
                          <h3 className="text-white font-medium truncate group-hover:text-indigo-400 transition-colors">
                            {ticket.title}
                          </h3>
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                            {ticket.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(ticket.created_at), "d MMM yyyy", { locale: nl })}
                            </span>
                            {ticket.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Gepland: {format(new Date(ticket.scheduled_date), "d MMM", { locale: nl })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {(() => {
                            const unread = studentUnread[ticket.id];
                            if (!unread) return null;
                            if (unread.chat_count > 0) {
                              return (
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500 text-white text-xs font-bold">
                                  {unread.chat_count}
                                </span>
                              );
                            }
                            if (unread.has_update) {
                              return (
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
                                  !
                                </span>
                              );
                            }
                            return null;
                          })()}
                          <Badge className={`status-${ticket.status} text-xs`}>
                            {statusLabels[ticket.status]}
                          </Badge>
                          <Badge className={`priority-${ticket.urgency} text-xs`}>
                            {ticket.urgency}
                          </Badge>
                          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all mt-2" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Floating Action Button */}
      <Link to="/nieuw-melding">
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="fixed bottom-6 right-6 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 rounded-full shadow-lg glow-primary flex items-center justify-center text-white z-40"
          data-testid="fab-new-report-btn"
        >
          <Plus className="w-6 h-6" />
        </motion.button>
      </Link>
    </div>
  );
};

export default StudentDashboard;
